/**
 * useLiveChannel — subscribe to a server-sent-events (SSE) channel and
 * receive real-time updates whenever the underlying data changes.
 *
 * The hook:
 *  - opens an EventSource to /v1/stream/channel/:channel
 *  - parses `snapshot` events (initial state) + `update` events (deltas)
 *  - auto-reconnects on `reconnect` events (5-min server-side rotation)
 *  - exposes `data`, `status`, and a manual `reconnect()`
 *  - invalidates a TanStack Query when data changes (so existing widgets
 *    don't have to be rewritten — they keep using useQuery and get the
 *    benefit of live updates)
 *
 * Usage:
 *   useLiveChannel('escalations', ['escalations']);
 *
 * The second arg is the query key to invalidate. When set, the underlying
 * useQuery refetches the moment the server reports a change.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getApiBase, getAccessToken } from '../api';

export type ChannelName = 'briefing' | 'escalations' | 'revenue' | 'actions' | 'anomalies' | 'cash-runway' | 'plans';

export type LiveStatus = 'connecting' | 'open' | 'closed' | 'error';

export interface LiveEvent<T = unknown> {
  channel: ChannelName;
  payload: T;
  hash: string;
  ts: string;
}

export function useLiveChannel<T = unknown>(
  channel: ChannelName,
  invalidateKey?: readonly unknown[],
  enabled: boolean = true,
): { data: LiveEvent<T> | null; status: LiveStatus; reconnect: () => void } {
  const [data, setData] = useState<LiveEvent<T> | null>(null);
  const [status, setStatus] = useState<LiveStatus>('closed');
  const [generation, setGeneration] = useState(0);
  const qc = useQueryClient();
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    async function open() {
      if (cancelled) return;
      setStatus('connecting');
      const token = await getAccessToken().catch(() => null);
      if (cancelled) return;
      if (!token) {
        setStatus('error');
        return;
      }

      // EventSource API doesn't allow custom headers — we pass the token via
      // a short-lived query param. Endpoint reads it as a fallback.
      const url = new URL(`${getApiBase()}/v1/stream/channel/${channel}`);
      url.searchParams.set('access_token', token);
      const es = new EventSource(url.toString());
      esRef.current = es;

      es.onopen = () => { if (!cancelled) setStatus('open'); };
      es.onerror = () => {
        if (cancelled) return;
        setStatus('error');
        es.close();
        // Exponential backoff with jitter
        const delay = 1000 + Math.floor(Math.random() * 2000);
        reconnectTimer = setTimeout(() => { if (!cancelled) setGeneration((g) => g + 1); }, delay);
      };

      const handler = (evt: MessageEvent) => {
        try {
          const parsed = JSON.parse(evt.data) as LiveEvent<T>;
          setData(parsed);
          if (invalidateKey) {
            void qc.invalidateQueries({ queryKey: invalidateKey as unknown[] });
          }
        } catch { /* malformed — ignore */ }
      };

      es.addEventListener('snapshot', handler as EventListener);
      es.addEventListener('update', handler as EventListener);
      es.addEventListener('reconnect', () => {
        if (cancelled) return;
        es.close();
        reconnectTimer = setTimeout(() => { if (!cancelled) setGeneration((g) => g + 1); }, 500);
      });
      es.addEventListener('heartbeat', () => { /* keep-alive */ });
    }

    void open();
    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      esRef.current?.close();
      esRef.current = null;
      setStatus('closed');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, enabled, generation]);

  return { data, status, reconnect: () => setGeneration((g) => g + 1) };
}
